import BaseView from './BaseView';

import { disablePageScroll, enablePageScroll } from 'scroll-lock';
import { gsap } from 'gsap';
import { SplitText } from 'gsap/SplitText';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import ProcessAnimationPartner from '../../components/ProcessAnimationPartner';
import ProcessAnimationSettings from '../../components/ProcessAnimationSettings';
import ProcessAnimationSubrosa from '../../components/ProcessAnimationSubrosa';
import ProcessAnimationTurns from '../../components/ProcessAnimationTurns';

gsap.registerPlugin(ScrollTrigger);
gsap.registerPlugin(SplitText);

let view = null;

class Home extends BaseView {
	constructor(container) {
		super(container);

		this.scrollTriggers = [];
	}

	init() {
		let mm = gsap.matchMedia();

		mm.add('(min-width: 768px)', () => {
			this.container.querySelectorAll('[data-value-card]').forEach((card) => {
				this.scrollTriggers.push(
					ScrollTrigger.create({
						trigger: card,
						start: 'top 45%',
						toggleClass: 'is-active',
					})
				);
			});
		});

		// Page Elements
		this.menu = document.querySelector('[data-menu-bar]');
		this.logo = this.container.querySelector('[data-home-logo]');
		this.illustration = this.container.querySelector('[data-home-illustration]');
		this.text = this.container.querySelector('[data-home-header-text]');
		this.title = this.container.querySelector('[data-home-title]');
		this.tagline = this.container.querySelector('[data-home-tagline]');

		// Process
		this.processAnimationPartner = this.container.querySelector('[data-process-animation-partner]');
		new ProcessAnimationPartner(this.processAnimationPartner);

		this.processAnimationSettings = this.container.querySelector('[data-process-animation-settings]');
		new ProcessAnimationSettings(this.processAnimationSettings);

		this.processAnimationSubrosa = this.container.querySelector('[data-process-animation-subrosa]');
		new ProcessAnimationSubrosa(this.processAnimationSubrosa);

		this.processAnimationTurns = this.container.querySelector('[data-process-animation-turns]');
		new ProcessAnimationTurns(this.processAnimationTurns);
	}

	skipIntroAnimation() {
		this.revealElements(true);
	}

	initIntroAnimation() {
		this.breakpoint = window.matchMedia('(min-width: 768px)');
		if (!this.breakpoint.matches) {
			this.skipIntroAnimation();
			return;
		}

		window.scrollTo(0, 0);

		this.boundScrollListener = this.handleScroll.bind(this);
		window.addEventListener('scroll', this.boundScrollListener);

		disablePageScroll();

		this.colorArray1 = [
			'rgb(129,148,206)',
			'rgb(216,227,215)',
			'rgb(217,86,233)',
			'rgb(82,103,40)',
			'rgb(253,246,199)',
			'rgb(236,107,80)',
			'rgb(129,222,199)',
			'rgb(129,67,57)',
		];

		this.colorArray2 = [
			'rgb(176,139,210)',
			'rgb(216,227,226)',
			'rgb(220,86,100)',
			'rgb(51,102,53)',
			'rgb(218,254,198)',
			'rgb(252,255,105)',
			'rgb(101,133,218)',
			'rgb(137,137,66)',
		];

		this.colorArray3 = [
			'rgb(154,213,191)',
			'rgb(226,227,214)',
			'rgb(76,80,229)',
			'rgb(98,57,34)',
			'rgb(245,195,203)',
			'rgb(234,85,225)',
			'rgb(147,223,110)',
			'rgb(126,57,124)',
		];

		this.dotPositionArray = [752, 537, 322, 107, -107, -322, -537, -752];
		this.charPositionArray = [350, 180, 380, 180, 40, -60, -240, -270, -700, -550];

		this.splitTitle();
		this.splitTagline();
		this.createDots();
		this.createDotTimeline();

		this.hideElements();
	}

	hideElements() {
		gsap.set(this.menu, {
			yPercent: -100,
			autoAlpha: 1,
		});

		gsap.set([this.illustration, this.text, this.tagline, this.splitTagline.chars], {
			autoAlpha: 0,
		});

		gsap.set(this.tagline, {
			autoAlpha: 1,
		});
	}

	revealElements(instant) {
		if (instant) {
			gsap.set([this.logo, this.menu, this.tagline, this.illustration, this.text], {
				autoAlpha: 1,
			});
			gsap.set(this.menu, {
				yPercent: 0,
				clearProps: 'transform',
			});
		} else {
			const tl = gsap.timeline();

			tl.to(this.illustration, {
				autoAlpha: 1,
				ease: 'sine.inOut',
				duration: 1.4,
				onComplete: () => {
					enablePageScroll();
				},
			});

			tl.to(this.menu, {
				yPercent: 0,
				duration: 0.8,
				ease: 'power2.inOut',
				clearProps: 'transform',
			});

			tl.to(
				this.text,
				{
					autoAlpha: 1,
					ease: 'sine.inOut',
					duration: 0.8,
				},
				'<'
			);
		}
	}

	splitTitle() {
		gsap.set(this.title, {
			y: window.innerHeight / 2 - (this.title.getBoundingClientRect().top + 60),
			scale: 0.7,
		});

		this.splitTitle = new SplitText(this.title, {
			type: 'words,chars',
			charsClass: 'char',
		});

		gsap.set(this.splitTitle.chars, {
			autoAlpha: 0,
		});
		gsap.set(this.logo, {
			autoAlpha: 1,
		});
	}

	splitTagline() {
		this.splitTagline = new SplitText(this.tagline, {
			type: 'words,chars',
			charsClass: 'char',
		});
	}

	createDots() {
		this.dotWrapper = document.createElement('div');
		this.dotWrapper.classList.add('dotwrapper');

		for (let step = 0; step < 8; step++) {
			this.dotWrapper.appendChild(document.createElement('span'));
		}

		this.title.appendChild(this.dotWrapper);
		this.dots = this.title.querySelectorAll('span');

		this.dots.forEach((dot, index) => {
			gsap.set(dot, {
				background: this.colorArray1[index],
			});
		});
	}

	createDotTimeline() {
		const dotTimeline = gsap.timeline({
			delay: 0.5,
			onComplete: () => {
				window.removeEventListener('scroll', this.boundScrollListener);
				this.createMoveToPositionTimeline();
			},
		});

		dotTimeline.timeScale(2.2);

		dotTimeline.addLabel('inital');

		dotTimeline.fromTo(
			this.title,
			{
				'--titleBlur': '3px',
			},
			{
				'--titleBlur': '0px',

				ease: 'power2.out',
				duration: 0.5,
			},
			'initial'
		);

		this.dots.forEach((dot, index) => {
			dotTimeline.from(
				dot,
				{
					xPercent: this.dotPositionArray[index] / 2,
					autoAlpha: 0,

					ease: 'power2.out',
					duration: 1,
				},
				'initial'
			);
		});

		dotTimeline.addLabel('left1');

		dotTimeline.to(
			this.title,
			{
				// Blur in
				'--titleBlur': '3px',

				ease: 'power2.in',
				duration: 0.5,
				delay: 0.5,
			},
			'left1'
		);

		dotTimeline.to(
			this.title,
			{
				// Blur out
				'--titleBlur': '0px',

				ease: 'power2.out',
				duration: 0.5,
				delay: 1,
			},
			'left1'
		);

		this.dots.forEach((dot, index) => {
			dotTimeline.to(
				dot,
				{
					xPercent: this.dotPositionArray[index],

					ease: 'power2.inOut',
					duration: 2,
				},
				'left1'
			);

			dotTimeline.set(
				dot,
				{
					background: this.colorArray2[index],
					delay: 1,
				},
				'left1'
			);
		});

		dotTimeline.addLabel('right1');

		dotTimeline.to(
			this.title,
			{
				// Blur in
				'--titleBlur': '3px',

				ease: 'power2.in',
				duration: 0.5,
				delay: 0.5,
			},
			'right1'
		);

		dotTimeline.to(
			this.title,
			{
				// Blur out
				'--titleBlur': '0px',

				ease: 'power2.out',
				duration: 0.5,
				delay: 1,
			},
			'right1'
		);

		this.dots.forEach((dot, index) => {
			dotTimeline.to(
				dot,
				{
					xPercent: 0,

					ease: 'power2.inOut',
					duration: 2,
				},
				'right1'
			);

			dotTimeline.set(
				dot,
				{
					background: this.colorArray3[index],
					delay: 1,
				},
				'right1'
			);
		});

		dotTimeline.addLabel('left2');

		dotTimeline.fromTo(
			this.title,
			{
				// Blur
				'--titleBlur': '0px',
			},
			{
				'--titleBlur': '6px',

				ease: 'power2.in',
				duration: 0.5,
				delay: 0.75,
			},
			'left2'
		);

		this.dots.forEach((dot, index) => {
			dotTimeline.to(
				dot,
				{
					xPercent: this.dotPositionArray[index] / 2,
					ease: 'power2.in',
					duration: 1.25,
				},
				'left2'
			);
		});

		dotTimeline.addLabel('center2');

		dotTimeline.set(
			this.dots,
			{
				autoAlpha: 0,
			},
			'center2'
		);

		dotTimeline.set(
			this.splitTitle.chars,
			{
				autoAlpha: 1,
			},
			'center2'
		);

		dotTimeline.fromTo(
			this.title,
			{
				'--titleBlur': '6px',
			},
			{
				'--titleBlur': '0px',

				ease: 'power2.out',
				duration: 1,
			},
			'center2'
		);

		this.splitTitle.chars.forEach((char, index) => {
			dotTimeline.from(
				char,
				{
					xPercent: this.charPositionArray[index],
					ease: 'power2.out',
					duration: 1.5,
				},
				'center2'
			);
		});
	}

	createMoveToPositionTimeline() {
		const positionTimeline = gsap.timeline();

		positionTimeline.to(this.title, {
			y: 0,
			scale: 1,

			ease: 'power2.inOut',
			duration: 0.8,
			delay: 0.4,
			onComplete: () => {
				this.revealElements();
			},
		});

		positionTimeline.from(
			this.tagline,
			{
				yPercent: -100,
				duration: 0.6,

				ease: 'power2.inOut',
			},
			0.5
		);

		positionTimeline.to(
			this.splitTagline.chars,
			{
				duration: 0.01,
				autoAlpha: 1,
				stagger: 0.05,
			},
			0.7
		);
	}

	handleScroll() {
		window.scrollTo(0, 0);
	}

	destroy() {
		this.scrollTriggers.forEach((trigger) => {
			trigger.kill();
		});

		this.scrollTriggers = [];

		window.removeEventListener('scroll', this.boundScrollListener);
	}
}

export default {
	namespace: 'home',
	beforeEnter: ({ current, next, trigger }) => {
		view = new Home(next.container);
		view.init();

		if (current.container != null) {
			view.skipIntroAnimation();
		} else {
			const visited = localStorage.getItem('visited');
			const now = new Date().getTime();
			console.log(now, visited);
			if (!visited || now > visited) {
				localStorage.setItem('visited', new Date().getTime() + (30 * 24 * 60 * 60 * 1000));
				view.initIntroAnimation();
			} else {
				view.skipIntroAnimation();
			}
		}

	},
	afterLeave: ({ current, next, trigger }) => {
		view.destroy();
		view = null;
	},
};
